/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { CartIconComponent, WishListIconComponent } from '@msdyn365-commerce/components';
import * as MsDyn365 from '@msdyn365-commerce/core';
import { ICartState } from '@msdyn365-commerce/global-state';
import { Customer } from '@msdyn365-commerce/retail-proxy';
import { ErrorNotification, NotificationsManager } from '@msdyn365-commerce-modules/notifications-core';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import {
    Button, getPayloadObject, getTelemetryAttributes, getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent, KeyCodes, Modal,
    ModalBody, ModalHeader, onTelemetryClick, Popover, TelemetryConstant
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { computed, reaction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { HeaderPreferredStore, Logo, NavIcon } from './components';
import { IHeaderData } from './header.data';
import { IHeaderProps, IHeaderResources, IMyAccountLinksData } from './header.props.autogenerated';

export interface IHeaderState {
    mobileMenuCollapsed: boolean;
    signinPopoverOpen: boolean;
    hasSigninerror?: boolean;
}

export interface IHeaderViewProps extends IHeaderProps<IHeaderData>, IHeaderState {
    logo: React.ReactNode;
    wishListIconDesktop: React.ReactNode;
    wishListIconMobile: React.ReactNode;
    cartIcon: React.ReactNode;
    navIcon: React.ReactNode;
    className: string;
    menuBar: React.ReactNode[];
    search: React.ReactNode[];
    siteOptions: React.ReactNode[];
    HeaderTag: IModuleProps;
    HeaderContainer: INodeProps;
    MobileMenuContainer: INodeProps;
    MobileMenuHeader: React.ReactNode;
    MobileMenuBodyContainer: INodeProps;
    MobileMenuLinksContainer: INodeProps;
    HeaderTopBarContainer: INodeProps;
    Divider: INodeProps;

    AccountInfoDropdownParentContainer?: INodeProps;
    AccountInfoDropdownPopoverConentContainer?: INodeProps;
    accountInfoDropdownButton?: React.ReactNode;

    signOutLink?: React.ReactNode;
    signInLink?: React.ReactNode;
    accountLinks?: React.ReactNode[];
    preferredStore?: React.ReactNode;
}

/**
 *
 * Header component.
 * @extends {React.PureComponent<IHeaderProps<IHeaderData>>}
 */
@observer
class Header extends React.PureComponent<IHeaderProps<IHeaderData>, IHeaderState> {
    private readonly popOverRef: React.RefObject<HTMLButtonElement>;

    private readonly telemetryContent: ITelemetryContent;

    private cart: ICartState | undefined;

    @computed public get displayName(): Readonly<string | undefined> {
        const customer = this.props.data.accountInformation?.result;
        return customer ? customer.FirstName || customer.Name : undefined;
    }

    public constructor(props: IHeaderProps<IHeaderData>) {
        super(props);
        this.popOverRef = React.createRef();
        this._toggleNavbar = this._toggleNavbar.bind(this);
        this._togglePopover = this._togglePopover.bind(this);
        this._keydown = this._keydown.bind(this);
        this.state = {
            mobileMenuCollapsed: true,
            signinPopoverOpen: false,
            hasSigninerror: false
        };
        this.telemetryContent = getTelemetryObject(this.props.context.request.telemetryPageName!, this.props.friendlyName, this.props.telemetry);
    }

    public componentDidMount(): void {
        if (MsDyn365.msdyn365Commerce.isBrowser) {
            window.addEventListener('keydown', this._keydown);
        }
        reaction(
            () => this.props.data.cart.result,
            () => {
                this.cart = this.props.data.cart.result;
            }
        );

        const {
            context: {
                request: {
                    user
                }
            },
            resources: {
                signUpCustomerNotFoundTitle,
                signUpMultipleCustomerFoundTitle,
                closeNotificationLabel
            }
        } = this.props;

        const signinNotificationError = this._getSigninNotification(user, signUpMultipleCustomerFoundTitle, signUpCustomerNotFoundTitle);
        if (signinNotificationError !== undefined && this.state.hasSigninerror === false) {
            // For signup show simple error notification.
            NotificationsManager.instance().addNotification(new ErrorNotification(
                signinNotificationError, closeNotificationLabel));
        }

    }

    public componentWillUnmount(): void {
        if (MsDyn365.msdyn365Commerce.isBrowser) {
            window.removeEventListener('keydown', this._keydown, false);
        }
    }

    public render(): JSX.Element | null {
        const {
            id,
            typeName,
            data: { accountInformation,
                wishlists
            },
            context: {
                request: {
                    user
                }
            },
            context,
            resources: {
                cartLabel,
                cartQtyLabel
            }
        } = this.props;
        const { logoLink, logoImage, shouldShowWishlistCount } = this.props.config;

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- app config is generic
        const disableTooltip = this.props.context.app.config.disableTooltip || false;
        const headerToggleClass = this.state.mobileMenuCollapsed ? '' : 'ms-header-open';
        const headerClassName = classnames('ms-header', this.props.config.className, headerToggleClass);
        const navbarKey = 'header-nav-mobile';
        const customer = accountInformation && accountInformation.result;
        const cartIconSlot = this._getSlot('cartIcon');
        const menuBarSlot = this._getSlot('menuBar');
        const searchSlot = this._getSlot('search');
        let wishlistCount = 0;
        const siteOptionsSlot = this._getSlot('siteOptions');
        const preferredStoreSlot = this._getSlot('storeSelector');
        if (wishlists !== undefined) {
            if (!wishlists.result || !ArrayExtensions.hasElements(wishlists.result) ||
                !wishlists.result[0].CommerceListLines || !ArrayExtensions.hasElements(wishlists.result[0].CommerceListLines)) {
                wishlistCount = 0;
            } else {
                wishlistCount = wishlists.result[0].CommerceListLines.length;
            }
        }
        const viewProps: IHeaderViewProps = {
            ...(this.props as IHeaderProps<IHeaderData>),
            ...(this.state),
            logo: <Logo {
                ...{ link: logoLink,
                    image: logoImage,
                    className: 'ms-header__logo',
                    gridSettings: this.props.context.request.gridSettings,
                    requestContext: this.props.context.request,
                    telemetryContent: this.telemetryContent,
                    typeName: this.props.typeName }} />,
            wishListIconDesktop: (
                <WishListIconComponent
                    className='ms-header__wishlist-desktop'
                    showButtonTooltip={!disableTooltip}
                    wishlistTooltipText={this.props.resources.wishlistTooltipText}
                    context={context}
                    id={id}
                    typeName={typeName}
                    telemetryContent={this.telemetryContent}
                    data={{}}
                    wishlistCount={wishlistCount}
                    wishlistCountLabel={this.props.resources.wishlistCountLabel}
                    isDispayWishlistCount={shouldShowWishlistCount}
                />
            ),
            wishListIconMobile: (
                <WishListIconComponent
                    className='ms-header__wishlist-mobile'
                    showButtonTooltip={!disableTooltip}
                    wishlistTooltipText={this.props.resources.wishlistTooltipText}
                    context={context}
                    id={id}
                    typeName={typeName}
                    telemetryContent={this.telemetryContent}
                    data={{}}
                    wishlistCount={wishlistCount}
                    wishlistCountLabel={this.props.resources.wishlistCountLabel}
                    isDispayWishlistCount={shouldShowWishlistCount}
                />
            ),
            cartIcon: (ArrayExtensions.hasElements(cartIconSlot)) ? cartIconSlot[0] : <CartIconComponent
                cartLabel={cartLabel} cartQtyLabel={cartQtyLabel} context={context}
                id={id} typeName={typeName} telemetryContent={this.telemetryContent}
                data={{ cart: this.cart }} />,
            navIcon: <NavIcon
                {
                    ...{
                        resources: this.props.resources,
                        isExpanded: !this.state.mobileMenuCollapsed,
                        targetId: navbarKey,
                        toggleNavBar: this._toggleNavbar,
                        telemetryContent: this.telemetryContent }}
            />,
            menuBar: ArrayExtensions.hasElements(menuBarSlot) ? menuBarSlot : [],
            search: ArrayExtensions.hasElements(searchSlot) ? searchSlot : [],
            siteOptions: ArrayExtensions.hasElements(siteOptionsSlot) ? siteOptionsSlot : [],
            className: headerClassName,
            HeaderTag: {
                moduleProps: this.props,
                className: classnames(headerClassName),
                tag: 'header'
            },
            HeaderContainer: {
                className: classnames('ms-header__container')
            },
            HeaderTopBarContainer: {
                className: classnames('ms-header__topbar')
            },
            Divider: {
                className: classnames('ms-header__divider')
            },
            MobileMenuContainer: {
                tag: Modal,
                id: navbarKey,
                className: 'ms-header__mobile-hamburger',
                isOpen: !this.state.mobileMenuCollapsed,
                wrapClassName: 'ms-header__modal',
                toggle: this._toggleNavbar
            },
            MobileMenuHeader: (
                <ModalHeader
                    className='ms-header__mobile-hamburger-menu-header'
                    toggle={this._toggleNavbar}
                />
            ),
            MobileMenuBodyContainer: {
                tag: ModalBody,
                className: 'ms-header__mobile-hamburger-menu-body'
            },
            MobileMenuLinksContainer: {
                className: 'ms-header__mobile-hamburger-menu-links'
            },
            AccountInfoDropdownParentContainer: {
                className: 'ms-header__account-info'
            },
            AccountInfoDropdownPopoverConentContainer: customer && this.displayName ? {
                tag: Popover,
                id: 'myprofilePopover',
                className: 'ms-header__account-info-content',
                placement: 'bottom',
                isOpen: this.state.signinPopoverOpen,
                target: this.popOverRef,
                toggle: this._togglePopover
            } : undefined,
            signInLink: this._getSigninButton(customer, user, this.props.resources),
            signOutLink: this._getSignOutButton(customer, user, this.props.resources),
            accountInfoDropdownButton: this._getAccountInfoDropdownButton(customer),
            accountLinks: this._getAccountLinks(customer),
            preferredStore: ArrayExtensions.hasElements(preferredStoreSlot) &&
                (<HeaderPreferredStore {...this.props} />)
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * Handle link text change.
     * @param linkIndex - Link Index.
     * @returns : Void.
     */
    public handleLinkTextChange = (linkIndex: number) => (event: MsDyn365.ContentEditableEvent): void => {
        if (this.props.config.myAccountLinks?.[linkIndex]) {
            this.props.config.myAccountLinks[linkIndex].linkText = event.target.value;
        }
    };

    /**
     * Get the slot.
     * @param slotName - Name of the slot.
     * @returns The slot.
     */
    private _getSlot(slotName: string): React.ReactNode[] | null {
        const { slots } = this.props;
        return (slots && slots[slotName] && slots[slotName].length && slots[slotName]) || null;
    }

    /**
     * Renders the sign in button.
     * @param customer - Customer object.
     * @param user - Request context user.
     * @param resources - Resources.
     * @returns React Node.
     */
    private _getSigninButton(customer: Customer | undefined, user: MsDyn365.IRequestContextUser | undefined, resources: IHeaderResources):
    React.ReactNode | undefined {
        const payLoad = getPayloadObject('click', this.telemetryContent, TelemetryConstant.SignIn);
        const attributes = getTelemetryAttributes(this.telemetryContent, payLoad);
        return !customer && (
            <Button
                className='ms-header__signin-button'
                title={resources.signInLinkText}
                href={user?.signInUrl}
                aria-label={resources.signInLinkAriaText}
                onClick={onTelemetryClick(this.telemetryContent, payLoad, 'Sign in')}
                {...attributes}
            >
                <span className='ms-header__signin-button-text' aria-hidden>
                    {resources.signInLinkText}
                </span>
            </Button>
        );
    }

    /**
     * Renders the sign out button.
     * @param customer - Customer object.
     * @param user - Request context user.
     * @param resources - Resources.
     * @returns React Node.
     */
    private _getSignOutButton(customer: Customer | undefined, user: MsDyn365.IRequestContextUser | undefined, resources: IHeaderResources):
    React.ReactNode | undefined {
        const payLoad = getPayloadObject('click', this.telemetryContent, TelemetryConstant.SignOut);
        const attributes = getTelemetryAttributes(this.telemetryContent, payLoad);
        return customer && this.displayName && (
            <Button
                className='ms-header__signout-button'
                title={resources.signOutLinkText}
                href={user?.signOutUrl ?? ''}
                aria-label={resources.signOutLinkAriaText}
                onClick={onTelemetryClick(this.telemetryContent, payLoad, 'Sign out')}
                {...attributes}
            >
                <span className='ms-header__signout-button-text' aria-hidden>
                    {resources.signOutLinkText}
                </span>
            </Button>
        );
    }

    private _getSigninNotification(user: MsDyn365.IRequestContextUser | undefined,
        signUpMultipleCustomerFoundTitle: string, signUpCustomerNotFoundTitle: string): string | undefined {
        const { signUpCustomerNotFound, signUpMultipleCustomerFound } = this.props.config;
        const notFoundError = 'Microsoft_Dynamics_Commerce_Runtime_CustomerNotFound_WhenAutoLinking';
        const multipleAccountError = 'Microsoft_Dynamics_Commerce_Runtime_MultipleCustomerAccountsFoundWithSameEmailAddress';

        if (user?.retailServerErrorCode === notFoundError) {
            return signUpCustomerNotFound !== undefined ? signUpCustomerNotFound : signUpCustomerNotFoundTitle;
        } else if (user?.retailServerErrorCode === multipleAccountError) {
            return signUpMultipleCustomerFound !== undefined ? signUpMultipleCustomerFound : signUpMultipleCustomerFoundTitle;
        }
        return undefined;
    }

    private _getAccountInfoDropdownButton(customer: Customer | undefined): React.ReactNode | undefined {
        const payLoad = getPayloadObject('click', this.telemetryContent, TelemetryConstant.MyProfile);
        const attributes = getTelemetryAttributes(this.telemetryContent, payLoad);
        return customer && this.displayName && (
            <Button
                innerRef={this.popOverRef}
                className='ms-header__profile-button'
                aria-describedby='myprofilePopover'
                onClick={this._togglePopover}
                color='link'
                aria-label={this.displayName}
                aria-expanded={this.state.signinPopoverOpen}
                {...attributes}
            >
                <span className='ms-profile-button-text'>
                    {this.displayName}
                </span>
            </Button>
        );
    }

    private _getAccountLinks(customer: Customer | undefined): React.ReactNode[] | undefined {
        const { myAccountLinks } = this.props.config;
        const payLoad = getPayloadObject('click', this.telemetryContent, '');
        if (!customer || !this.displayName || !myAccountLinks || !ArrayExtensions.hasElements(myAccountLinks)) {
            return undefined;
        }
        return myAccountLinks.map((cta: IMyAccountLinksData, index: number) => {
            payLoad.contentAction.etext = cta.linkText;
            const attributes = getTelemetryAttributes(this.telemetryContent, payLoad);
            const editableLink: MsDyn365.ILinksData = {
                ariaLabel: cta.ariaLabel,
                className: 'ms-signin-info__account-link-button',
                linkText: cta.linkText,
                linkUrl: cta.linkUrl.destinationUrl,
                openInNewTab: cta.openInNewTab,
                role: 'link',
                additionalProperties: attributes
            };

            return (
                <MsDyn365.Link
                    key={index}
                    link={editableLink}
                    editProps={{ onTextChange: this.handleLinkTextChange(index), requestContext: this.props.context.request }}
                />
            );
        });
    }

    private _toggleNavbar(): void {
        this.setState({
            mobileMenuCollapsed: !this.state.mobileMenuCollapsed
        });
    }

    private _togglePopover(): void {
        this.setState({
            signinPopoverOpen: !this.state.signinPopoverOpen
        });
    }

    private _keydown(event: KeyboardEvent): void {
        if (event.keyCode === KeyCodes.Escape) {
            this.setState({ mobileMenuCollapsed: true });
        }
    }
}

export default Header;
